import { useUser } from '@gat-ship/user'
import { accessCodes, type Stakeholder } from '@gat-ship/types'

/**
 * Visibility predicates for stakeholder actions: the single gate for whether
 * the current user may create, edit, remove or restore a stakeholder. Pure
 * checks only — opening modals and running the mutation is the call site's job
 * (via the stakeholder service `commands`). Mirrors
 * `useSupportSessionPermissions`.
 *
 * The gate combines the role permission with the stakeholder's active state:
 * editing and removing require an *active* stakeholder, restoring requires an
 * *inactive* one. Both edit-style actions share the `modifyStakeholder`
 * permission; creation has its own `addStakeholder` permission.
 */
export function useStakeholderPermissions() {
  const user = useUser()

  /** Create a new stakeholder of any type. */
  function canCreate() {
    return user.hasPermission(accessCodes.system.stakeholder.addStakeholder)
  }

  /** Edit an active stakeholder (general info, addresses, billing, numbers). */
  function canModify(stakeholder: Stakeholder) {
    return (
      user.hasPermission(accessCodes.system.stakeholder.modifyStakeholder) &&
      !stakeholder.isInactive
    )
  }

  /** Remove (deactivate) an active stakeholder. */
  function canRemove(stakeholder: Stakeholder) {
    return canModify(stakeholder)
  }

  /** Restore a previously removed (inactive) stakeholder. */
  function canRestore(stakeholder: Stakeholder) {
    return (
      user.hasPermission(accessCodes.system.stakeholder.modifyStakeholder) &&
      stakeholder.isInactive
    )
  }

  /** Add a contact (person or point) to an active stakeholder. */
  function canAddContact(stakeholder: Stakeholder) {
    return (
      user.hasPermission(accessCodes.system.stakeholder.addContact) &&
      !stakeholder.isInactive
    )
  }

  /** Remove a contact from a stakeholder. */
  function canRemoveContact() {
    return user.hasPermission(accessCodes.system.stakeholder.removeContact)
  }

  return {
    canCreate,
    canModify,
    canRemove,
    canRestore,
    canAddContact,
    canRemoveContact
  }
}
